/**
 * HotspotsList — W1.2 / B8.1
 *
 * Page-Komponente: orchestriert Filters + Row-Liste + Pagination + States.
 * Filter-State kommt als Props (aus Route.useSearch) und wird per
 * navigate() in die URL geschrieben.
 * B8.1: "Hotspot hinzufügen"-Button oben rechts + AddHotspotModal.
 *
 * Design-Fix (Abnahme P1, hotspots.html): Struktur 1:1 an den Referenz-
 * Prototyp angeglichen — Breadcrumb statt Tab-Leiste, nur 3 Dropdowns
 * (Status/Type/Tag) + Freitext statt Segment-Pillen/Sort/Columns/View-Toggle,
 * feste Spaltenreihung mit Kopfzeile statt Spalten-Sichtbarkeits-Pillen.
 */
import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation, Trans } from 'react-i18next'
import { useHotspots } from './useHotspots'
import { useAffiliatedHotspots } from './useAffiliatedHotspots'
import { HotspotRow } from './HotspotRow'
import { formatHnt, STATUS_PILL_VARIANT, statusLabel } from './hotspotRowFormat'
import { formatLocalDate } from '../../lib/locale'
import { HotspotFilters } from './HotspotFilters'
import { AddHotspotModal } from './AddHotspotModal'
import { Pagination } from '../../components/Pagination'
import { PlanLimitBanner } from '../../components/PlanLimitBanner'
import type { HotspotsFilters, Hotspot } from './useHotspots'
import { Link } from '@tanstack/react-router'
import { useTags } from '../tags/useTags'
import { StatusPill } from '../../components/StatusPill'
import { useRemoveHotspot } from './useRemoveHotspot'
import { useArchiveHotspot, useUnarchiveHotspot } from './useArchiveHotspot'
import { quotaInfoFromError } from './quotaError'
import { toast } from '../../components/Toast/useToast'
import { hotspotDisplayName } from '../../lib/hotspotDisplayName'
import './hotspots-list.css'

// Extended Hotspot type to support optional tag_names field (v1 client-side filter)
// Tech-Debt S12: remove once API returns tag_names in hotspot index response
type HotspotWithTags = Hotspot & { tag_names?: string[] }

type Props = {
  filters: HotspotsFilters
}

// --- Loading Skeleton ---

function SkeletonRow() {
  return (
    <div className="list-row skeleton" aria-hidden="true">
      <div className="skeleton-line identity-placeholder" />
      <div className="skeleton-line status-placeholder" />
      <div className="skeleton-line reward-placeholder" />
    </div>
  )
}

function LoadingSkeleton() {
  const { t } = useTranslation('hotspots')
  return (
    <div aria-busy="true" aria-label={t('list.loadingLabel')}>
      {Array.from({ length: 4 }).map((_, i) => (
        <SkeletonRow key={i} />
      ))}
    </div>
  )
}

// --- Error State ---

type ErrorStateProps = { onRetry: () => void }

function ErrorState({ onRetry }: ErrorStateProps) {
  const { t } = useTranslation('hotspots')
  return (
    <div className="error-state" role="alert">
      <p>{t('list.errorMsg')}</p>
      <button className="btn btn-primary" onClick={onRetry}>
        {t('common:actions.retry')}
      </button>
    </div>
  )
}

// --- Empty State ---

function EmptyState({ onAdd }: { onAdd: () => void }) {
  const { t } = useTranslation('hotspots')
  return (
    <div className="empty-state">
      <p>{t('list.emptyMsg')}</p>
      <button
        type="button"
        className="btn btn-primary"
        onClick={onAdd}
        data-testid="empty-add-hotspot"
      >
        {t('list.addBtn')}
      </button>
    </div>
  )
}

// --- Header (Prototyp hotspots.html: Breadcrumb + Titel + Status-Pillen + Map/Add-Buttons) ---

type ListHeaderProps = {
  hotspots: Hotspot[]
  isLoading: boolean
  onAdd: () => void
  isAffiliate: boolean
  onToggleAffiliate: () => void
  isArchivedView: boolean
  onToggleArchivedView: () => void
}

function ListHeader({
  hotspots,
  isLoading,
  onAdd,
  isAffiliate,
  onToggleAffiliate,
  isArchivedView,
  onToggleArchivedView,
}: ListHeaderProps) {
  const { t } = useTranslation('hotspots')

  const online = hotspots.filter((h) => h.status === 'online').length
  const degraded = hotspots.filter((h) => h.status === 'warn').length
  const offline = hotspots.filter((h) => h.status === 'offline').length
  const rewards24h = hotspots.reduce((sum, h) => sum + (h.hnt_24h ?? 0), 0)

  return (
    <div>
      {/* Breadcrumb (Prototyp: globale Topbar-Crumbs "Hotspots > My Hotspots"; die
          App-Topbar (AppShell, Foundation-Besitz) hat noch keinen Breadcrumb-Slot,
          deshalb hier lokal auf der Seite — dieselben Keys wie HotspotDetail.tsx.
          WP10 (Abnahme-Review): die frühere Tab-Leiste bleibt bewusst entfernt
          (Design-Fix P1), aber die /affiliated-Ansicht braucht einen sichtbaren
          Einstiegspunkt statt nur per Deep-Link erreichbar zu sein — deshalb hier
          ein diskreter Umschalt-Link statt einer neuen Tab-Reihe. */}
      <nav
        aria-label="Breadcrumb"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 13,
          marginBottom: 8,
          flexWrap: 'wrap',
        }}
      >
        <span className="muted">{t('detail.breadcrumbHotspots')}</span>
        <span className="muted" aria-hidden="true">
          ›
        </span>
        <span style={{ fontWeight: 500 }}>
          {isAffiliate
            ? t('list.affiliated.breadcrumb')
            : isArchivedView
              ? t('list.archive.tabArchived')
              : t('detail.breadcrumbMyHotspots')}
        </span>
        <span style={{ flex: 1 }} />
        {/* Issue #39: zweiter, ebenso diskreter Umschalt-Link fuer die
            Archiv-Ansicht -- nur sichtbar, solange man nicht in der
            Gehostet-Ansicht ist (archiviert betrifft nur eigene Hotspots). */}
        {!isAffiliate && (
          <button
            type="button"
            className="btn-link"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              color: 'var(--accent, #3b82f6)',
              cursor: 'pointer',
            }}
            onClick={onToggleArchivedView}
            data-testid="archived-toggle-link"
          >
            {isArchivedView ? t('list.archive.tabActive') : t('list.archive.tabArchived')}
          </button>
        )}
        {!isArchivedView && (
          <button
            type="button"
            className="btn-link"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              color: 'var(--accent, #3b82f6)',
              cursor: 'pointer',
            }}
            onClick={onToggleAffiliate}
            data-testid="affiliated-toggle-link"
          >
            {isAffiliate ? t('list.affiliated.backToMineLink') : t('list.affiliated.viewLink')}
          </button>
        )}
      </nav>
      <div
        className="section-header"
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
          marginBottom: '16px',
        }}
      >
        <div>
          <h1 style={{ margin: '0 0 6px', fontSize: '22px' }}>
            {isAffiliate ? t('list.affiliated.title') : t('list.title')}
          </h1>
          {isLoading ? (
            <div className="skeleton-line" style={{ width: '200px', height: '20px' }} />
          ) : (
            <div className="row" style={{ gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <StatusPill variant="success" label={t('hero.countOnline', { count: online })} />
              <StatusPill variant="warning" label={t('hero.countDegraded', { count: degraded })} />
              <StatusPill variant="danger" label={t('hero.countOffline', { count: offline })} />
              <span style={{ fontSize: '12px', color: 'var(--text-subtle)' }}>
                · {t('list.fleetCaption', { amount: rewards24h.toFixed(3) })}
              </span>
            </div>
          )}
        </div>
        <div className="row" style={{ gap: '8px' }}>
          <Link to="/hotspots/map" className="btn btn-secondary">
            {t('list.mapViewBtn')}
          </Link>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onAdd}
            data-testid="open-add-hotspot-modal-header"
          >
            {t('list.addBtnShort')}
          </button>
        </div>
      </div>
    </div>
  )
}

// --- List column header row (Prototyp hotspots.html .hs-row.hs-head) ---

function ListColumnHead() {
  const { t } = useTranslation('hotspots')
  return (
    <div className="list-row hs-head" aria-hidden="true">
      <div className="hs-cell-identity">{t('list.head.hotspot')}</div>
      <div className="hs-cell-status">{t('list.head.status')}</div>
      <div className="hs-cell-type hs-col-md">{t('list.head.type')}</div>
      <div className="hs-cell-spark hs-col-md">{t('list.head.trend')}</div>
      <div className="reward-cols hs-col-sm">
        <span className="num-sm">{t('list.rewards.yesterday')}</span>
        <span className="num-sm">7d</span>
        <span className="num-sm">30d</span>
      </div>
      <div className="hs-cell-gb hs-col-md">{t('list.head.gb24h')}</div>
      <div className="hs-col-chevron" />
    </div>
  )
}

// --- Archivierte Hotspots (Issue #39) ---
// Eigene Reihe (analog WatchedRow), weil HotspotRow ein <a>-Wrapper ist und
// die Reaktivieren-Aktion + Herkunfts-Badge eine eigene, einfachere Struktur
// braucht statt HotspotRow weiter zu verzweigen.

type ArchivedRowProps = { hotspot: Hotspot }

function ArchivedRow({ hotspot }: ArchivedRowProps) {
  const { t } = useTranslation('hotspots')
  const unarchive = useUnarchiveHotspot()
  const displayName = hotspotDisplayName(hotspot.name, hotspot.address, t('list.unknownHotspot'))

  async function handleUnarchive() {
    try {
      await unarchive.mutateAsync(hotspot.id ?? '')
      toast.success(t('list.archive.unarchiveToast'))
    } catch (err) {
      const quotaInfo = quotaInfoFromError(err)
      if (quotaInfo) {
        toast.error(
          quotaInfo.deviceType && quotaInfo.used !== undefined && quotaInfo.limit !== undefined
            ? t('list.archive.quotaToast', {
                deviceType: quotaInfo.deviceType,
                used: quotaInfo.used,
                limit: quotaInfo.limit,
              })
            : t('list.archive.quotaToastGeneric'),
        )
        return
      }
      toast.error(t('list.archive.unarchiveError'))
    }
  }

  return (
    <div className="list-row" data-testid={`archived-hotspot-row-${hotspot.id}`}>
      <Link
        to="/hotspots/$id"
        params={{ id: String(hotspot.id) }}
        className="hs-cell-identity"
        style={{ flex: '1 1 220px', minWidth: 0 }}
      >
        <div className="identity-title ellipsis">{displayName}</div>
        <div className="identity-sub">{hotspot.city?.name ?? ''}</div>
      </Link>
      <span className="pill" data-testid={`archived-hotspot-reason-${hotspot.id}`}>
        {hotspot.archived_reason === 'manual'
          ? t('list.archive.reasonManual')
          : t('list.archive.reasonSystem')}
      </span>
      <div className="reward-cols" style={{ flex: '1 1 auto' }}>
        <span className="num-sm">
          <span className="label-xs muted">{t('list.rewards.yesterday')}</span> {formatHnt(hotspot.hnt_24h)}
        </span>
        <span className="num-sm hs-col-sm">
          <span className="label-xs muted">7d</span> {formatHnt(hotspot.hnt_7d)}
        </span>
        <span className="num-sm hs-col-sm">
          <span className="label-xs muted">30d</span> {formatHnt(hotspot.hnt_30d)}
        </span>
      </div>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={handleUnarchive}
        disabled={unarchive.isPending}
        data-testid={`archived-hotspot-unarchive-${hotspot.id}`}
      >
        {t('list.archive.unarchiveBtn')}
      </button>
    </div>
  )
}

function ArchivedSection({ hotspots }: { hotspots: Hotspot[] }) {
  const { t } = useTranslation('hotspots')
  if (hotspots.length === 0) {
    return <p className="muted">{t('list.archive.emptyMsg')}</p>
  }

  return (
    <div className="list" role="list" aria-label={t('list.archive.tabArchived')}>
      {hotspots.map((h) => (
        <ArchivedRow key={h.id} hotspot={h} />
      ))}
    </div>
  )
}

// --- Watched hotspots section (Prototyp hotspots.html #watched-list) ---
// Hotspots, die per user_hotspots.watched=true beobachtet werden: fremdes
// Eigentum, read-only, zaehlt nicht gegen die Quota. Kein eigener API-
// Endpunkt vorgesehen (SPEC.md) — Teilmenge derselben /hotspots-Antwort.
//
// WP11 (Deep-Review 04.09.): Unwatch entfernt die UserHotspot-Zuordnung ueber
// den bestehenden Entfernen-Endpunkt (DELETE /api/v2/hotspots/{id},
// useRemoveHotspot) statt nur PATCH watched:false zu senden — sonst blieb bei
// Alt-Datensaetzen aus dem frueheren Claim-basierten Watch-Flow ein
// faelschlich gesetztes owned:true bestehen und der Hotspot erschien danach
// als "eigen".

type WatchedRowProps = { hotspot: Hotspot }

function WatchedRow({ hotspot }: WatchedRowProps) {
  const { t } = useTranslation('hotspots')
  const removeHotspot = useRemoveHotspot()
  const displayName = hotspotDisplayName(hotspot.name, hotspot.address, t('list.unknownHotspot'))

  async function handleUnwatch() {
    try {
      await removeHotspot.mutateAsync(hotspot.id ?? '')
      toast.success(t('list.watched.unwatchToast'))
    } catch {
      toast.error(t('list.watched.unwatchError'))
    }
  }

  return (
    <div className="list-row" data-testid={`watched-hotspot-row-${hotspot.id}`}>
      <Link
        to="/hotspots/$id"
        params={{ id: String(hotspot.id) }}
        className="hs-cell-identity"
        style={{ flex: '1 1 220px', minWidth: 0 }}
      >
        <div className="identity-title ellipsis">{displayName}</div>
        <div className="identity-sub">{hotspot.city?.name ?? ''}</div>
      </Link>
      <StatusPill
        variant={STATUS_PILL_VARIANT[hotspot.status ?? 'offline']}
        label={statusLabel(hotspot.status ?? 'offline', hotspot.online_pct ?? 0, t)}
      />
      <span className="hs-owner">{t('list.watched.externalOwner')}</span>
      <span className="hs-since">{formatLocalDate(hotspot.added_at)}</span>
      <div className="reward-cols" style={{ flex: '1 1 auto' }}>
        <span className="num-sm">
          <span className="label-xs muted">{t('list.rewards.yesterday')}</span> {formatHnt(hotspot.hnt_24h)}
        </span>
        <span className="num-sm hs-col-sm">
          <span className="label-xs muted">7d</span> {formatHnt(hotspot.hnt_7d)}
        </span>
        <span className="num-sm hs-col-sm">
          <span className="label-xs muted">30d</span> {formatHnt(hotspot.hnt_30d)}
        </span>
      </div>
      <button
        type="button"
        className="btn btn-ghost btn-icon"
        title={t('list.watched.unwatchBtn')}
        aria-label={t('list.watched.unwatchBtn')}
        onClick={handleUnwatch}
        disabled={removeHotspot.isPending}
        data-testid={`watched-hotspot-unwatch-${hotspot.id}`}
      >
        &times;
      </button>
    </div>
  )
}

function WatchedSection({ hotspots }: { hotspots: Hotspot[] }) {
  const { t } = useTranslation('hotspots')
  if (hotspots.length === 0) return null

  return (
    <section className="hotspots-watched-section" aria-label={t('list.watched.sectionTitle')}>
      <div className="section-title" style={{ marginTop: '28px' }}>
        <h3 style={{ fontSize: '15px' }}>{t('list.watched.sectionTitle')}</h3>
        <span className="pill pill-accent">{hotspots.length}</span>
      </div>
      <div style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '-6px 0 12px' }}>
        {t('list.watched.sectionDesc')}
      </div>
      <div className="card">
        {hotspots.map((h) => (
          <WatchedRow key={h.id} hotspot={h} />
        ))}
      </div>
    </section>
  )
}

// --- Legacy Banner ---

type LegacyBannerProps = { count: number }

function LegacyBanner({ count }: LegacyBannerProps) {
  return (
    <div className="legacy-banner" role="note">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: '8px' }}>
        <path d="M7 0L14 14H0L7 0Z" fill="currentColor" />
      </svg>
      <span>
        {/* Inline-<strong>-Markup via <Trans> erhalten; {{count}} interpoliert. */}
        <Trans
          i18nKey="list.legacyBanner"
          ns="hotspots"
          values={{ count }}
          components={{ strong: <strong /> }}
        />
      </span>
    </div>
  )
}

// --- Filter Helper (client-side for device_type) ---

function filterHotspots(hotspots: Hotspot[], deviceType?: string): Hotspot[] {
  if (!deviceType || deviceType === 'all') return hotspots
  return hotspots.filter((h) => {
    if (deviceType === 'wifi') {
      return h.device_type === 'wifi' || h.device_type === 'wifiOutdoor'
    }
    return h.device_type === deviceType
  })
}

// --- Main Component ---

export function HotspotsList({ filters }: Props) {
  const { t } = useTranslation('hotspots')
  const navigate = useNavigate()

  // Beide Hooks unbedingt aufrufen (rules-of-hooks); enabled-Flag verhindert Doppel-Fetch.
  const isAffiliate = filters.filter === 'affiliate'
  // Issue #39: 'inactive' war seit F-033 als vierter Tab-Wert reserviert
  // (My/Affiliated/Inactive/Neighbourhood), bisher aber ungenutzt -- das ist
  // jetzt die Archiv-Ansicht.
  const isArchivedView = filters.filter === 'inactive'
  const affiliated = useAffiliatedHotspots(isAffiliate)
  const regular = useHotspots(
    { ...filters, archived_only: isArchivedView, per_page: 500 },
    !isAffiliate,
  )
  const { data, meta, isLoading, isError, refetch } = isAffiliate ? affiliated : regular

  const archiveHotspot = useArchiveHotspot()
  const [archivingId, setArchivingId] = useState<string | number | null>(null)

  async function handleArchive(id: string | number) {
    setArchivingId(id)
    try {
      await archiveHotspot.mutateAsync(id)
      toast.success(t('list.archive.archiveToast'))
    } catch {
      toast.error(t('list.archive.archiveError'))
    } finally {
      setArchivingId(null)
    }
  }

  const { data: allTags } = useTags()

  const [addModalOpen, setAddModalOpen] = useState(false)
  // AC5: Tag-Filter v1 (client-side) — Tech-Debt S12: Server-Side-Filter
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null)

  const currentPage = filters.page ?? 1
  const pagy = meta?.pagy

  // Client-side filtering. Reihenfolge kommt vom Server (sort_by/sort_dir).
  const processedHotspots = useMemo(() => {
    if (!data) return []
    let filtered = isAffiliate ? data : filterHotspots(data, filters.device_type)
    // Prototyp (hotspots.html): beobachtete (fremde) Hotspots stehen exklusiv
    // in der Watched-Sektion unten, nie zusaetzlich in der eigenen Liste.
    // Archiv-Ansicht liefert ohnehin nur eigene (owned) Zeilen, watched ist
    // dort irrelevant.
    if (!isAffiliate && !isArchivedView) {
      filtered = filtered.filter((h) => h.watched !== true)
    }
    // AC5: Tag-Filter v1 — filter by tag_names field if present on hotspot
    // Tech-Debt S12: replace with server-side filter; tag_names not yet in API schema
    if (selectedTagId !== null && allTags) {
      const selectedTag = allTags.find((t) => t.id === selectedTagId)
      if (selectedTag) {
        filtered = filtered.filter((h) => {
          const hw = h as HotspotWithTags
          return hw.tag_names?.includes(selectedTag.name) ?? false
        })
      }
    }
    return filtered
  }, [data, filters.device_type, isAffiliate, isArchivedView, selectedTagId, allTags])

  // Paginate processed hotspots
  const pageSize = 25
  // Use server-side page count when available (pagy.pages reflects total pages from API),
  // otherwise fall back to client-side calculation
  const totalPages = pagy?.pages ?? Math.ceil(processedHotspots.length / pageSize)
  const paginatedHotspots = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return processedHotspots.slice(start, start + pageSize)
  }, [processedHotspots, currentPage])

  function handlePageChange(page: number) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(navigate as any)({
      search: (prev: Record<string, unknown>) => ({ ...prev, page }),
    })
  }

  // WP10: diskreter Umschalt-Einstiegspunkt fuer die /affiliated-Ansicht
  // (Filter owned|watched|affiliate) statt reiner Deep-Link-Erreichbarkeit.
  function handleToggleAffiliate() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(navigate as any)({
      search: (prev: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...prev, page: 1 }
        if (isAffiliate) {
          delete next.filter
        } else {
          next.filter = 'affiliate'
        }
        return next
      },
    })
  }

  // Issue #39: gleiches Muster wie handleToggleAffiliate, anderer Filterwert.
  function handleToggleArchivedView() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(navigate as any)({
      search: (prev: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...prev, page: 1 }
        if (isArchivedView) {
          delete next.filter
        } else {
          next.filter = 'inactive'
        }
        return next
      },
    })
  }

  const legacyCount = data?.filter((h) => h.era === 'pre').length ?? 0
  const watchedHotspots = data?.filter((h) => h.watched === true) ?? []

  return (
    <main className="hotspots-list-page">
      <ListHeader
        hotspots={data ?? []}
        isLoading={isLoading}
        onAdd={() => setAddModalOpen(true)}
        isAffiliate={isAffiliate}
        onToggleAffiliate={handleToggleAffiliate}
        isArchivedView={isArchivedView}
        onToggleArchivedView={handleToggleArchivedView}
      />

      <AddHotspotModal isOpen={addModalOpen} onClose={() => setAddModalOpen(false)} />

      {/* Prototyp hotspots.html .filters: Freitext + 3 Dropdowns (Status/Type/Tag) + Count */}
      <div className="filterbar" style={{ marginBottom: '10px' }}>
        <HotspotFilters currentFilters={filters} />
        {allTags && allTags.length > 0 && (
          <select
            className="select"
            data-testid="tag-filter-select"
            value={selectedTagId ?? ''}
            onChange={(e) => {
              const val = e.target.value
              setSelectedTagId(val === '' ? null : Number(val))
            }}
            aria-label={t('list.tagFilterLabel')}
          >
            <option value="">{t('list.allTags')}</option>
            {allTags.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
          </select>
        )}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: '12px', color: 'var(--text-subtle)' }}>
          {t('filters.countLabel', { count: processedHotspots.length, total: data?.length ?? 0 })}
        </span>
      </div>

      {/* Issue #57: eigene Hotspots ueber dem Plan-Kontingent werden vom BE
          ausgeblendet — ohne Hinweis sah die Liste nach Datenverlust aus. */}
      <PlanLimitBanner planLimit={meta?.plan_limit} />

      {legacyCount > 0 && <LegacyBanner count={legacyCount} />}

      <section aria-label={t('list.sectionLabel')}>
        {isLoading && <LoadingSkeleton />}

        {isError && !isLoading && <ErrorState onRetry={refetch} />}

        {/* Issue #39: Archiv-Ansicht hat eine eigene Leer-/Zeilen-Darstellung
            (kein "Hotspot hinzufuegen"-CTA, Reaktivieren statt Chevron). */}
        {!isLoading && !isError && isArchivedView && (
          <ArchivedSection hotspots={paginatedHotspots} />
        )}

        {!isLoading && !isError && !isArchivedView && processedHotspots.length === 0 && (
          <EmptyState onAdd={() => setAddModalOpen(true)} />
        )}

        {!isLoading && !isError && !isArchivedView && processedHotspots.length > 0 && (
          <div className="list" role="list" aria-label={t('list.listLabel')}>
            <ListColumnHead />
            {paginatedHotspots.map((hotspot) => (
              <HotspotRow
                key={hotspot.id}
                hotspot={hotspot}
                onArchive={handleArchive}
                isArchiving={archivingId === hotspot.id}
              />
            ))}
          </div>
        )}
      </section>

      {totalPages > 1 && (
        <Pagination
          page={currentPage}
          pages={totalPages}
          count={processedHotspots.length}
          onPageChange={handlePageChange}
        />
      )}

      {/* Prototyp hotspots.html: eigener Abschnitt fuer beobachtete (fremde)
          Hotspots, immer sichtbar, unabhaengig von den Filtern oben -- ausser
          in der Archiv-Ansicht, die ihre eigene, unabhaengige Liste zeigt. */}
      {!isArchivedView && <WatchedSection hotspots={watchedHotspots} />}
    </main>
  )
}
